/**
 * Cadena aguas arriba / aguas abajo de un nodo.
 *
 * Es la MISMA regla que usa el backend para armar el subgrafo de una
 * ejecución parcial (`app/mcp/graph.py::ancestors_of / descendants_of`):
 * si cambia una, tiene que cambiar la otra.
 *
 * Todo en memoria: con 90 nodos esto corre en microsegundos, sin pedirle
 * nada al server.
 */

const indexEdges = (edges) => {
  const parents = new Map();
  const children = new Map();
  for (const e of edges || []) {
    if (!e?.source || !e?.target) continue;
    if (!parents.has(e.target)) parents.set(e.target, []);
    parents.get(e.target).push(e.source);
    if (!children.has(e.source)) children.set(e.source, []);
    children.get(e.source).push(e.target);
  }
  return { parents, children };
};

const walk = (startIds, adjacency) => {
  const seen = new Set();
  const stack = [...startIds];
  while (stack.length) {
    const next = adjacency.get(stack.pop());
    if (!next) continue;
    for (const id of next) {
      if (!seen.has(id)) {
        seen.add(id);
        stack.push(id);
      }
    }
  }
  return seen;
};

export const ancestorsOf = (nodeIds, edges) =>
  walk(nodeIds, indexEdges(edges).parents);

export const descendantsOf = (nodeIds, edges) =>
  walk(nodeIds, indexEdges(edges).children);

/**
 * Cadena completa de un nodo, en dos tonos: de dónde viene y qué alimenta.
 * Devuelve sets de ids y el set de edges que forman parte del camino.
 */
export const chainOf = (nodeId, edges) => {
  if (!nodeId) return null;
  const { parents, children } = indexEdges(edges);
  const upstream = walk([nodeId], parents);
  const downstream = walk([nodeId], children);
  const inChain = new Set([nodeId, ...upstream, ...downstream]);
  const upstreamEdges = new Set();
  const downstreamEdges = new Set();
  for (const e of edges || []) {
    if (!e?.id) continue;
    // Una arista es "aguas arriba" si su destino está en la cadena de
    // entrada (o es el nodo mismo); "aguas abajo" en el caso simétrico.
    const upHit = (upstream.has(e.source) || e.source === nodeId)
      && (upstream.has(e.target) || e.target === nodeId);
    const downHit = (downstream.has(e.target) || e.target === nodeId)
      && (downstream.has(e.source) || e.source === nodeId);
    if (upHit) upstreamEdges.add(e.id);
    else if (downHit) downstreamEdges.add(e.id);
  }
  return { nodeId, upstream, downstream, inChain, upstreamEdges, downstreamEdges };
};

/** Número corto que se muestra en el chip: node_21 → 21. */
export const shortNodeNumber = (nodeId) => {
  const match = /(\d+)\s*$/.exec(String(nodeId || ''));
  return match ? match[1] : String(nodeId || '');
};
